import { call, takeLatest, select, put } from 'redux-saga/effects';
import { merge } from 'lodash';
import {
  getThresholdData,
  updateThresholdData,
  updateThresholdDisabledStatus,
} from '@api/metricContainer';
import getFeatures from '@api/features';
import { updateThresholdChangeStatus, setAdminProfile } from '@store/noc/base/actions';
import { updateFeatures } from '@store/common/actions';
import { autoRequest, createAllActions } from '@api/createRequestStore';
import { selectedUserRoleSelector } from '@store/common/selectors';
import transformData from '@components/noc-metric-panel/utils/transformData';
import { modes, NOC_URL } from '@components/noc-metric-panel/utils/constants';
import featureNames from '@utils/featureToggle/constants';

import { message } from 'antd';
import * as types from './types';
import {
  thresholdPayloadSelector,
  thresholdFilterSelector,
  trendsSavePayloadSelector,
  filtersSavePayloadSelector,
} from './selectors';
import { nocModeSelector } from '../base/selectors';

const DISABLE_THRESHOLD_FEATURE_FLAG = featureNames.DISABLE_THRESHOLD;
const ADMIN_ROLE_NAME = 'Admin';

function* getFetchingActions() {
  const mode = yield select(nocModeSelector);
  const { fetching, fetchingFail, fetchingSuccess } =
    mode === modes.TRENDS
      ? createAllActions(types.TRENDS_THRESHOLD_SCOPE)
      : createAllActions(types.FILTERS_THRESHOLD_SCOPE);
  return { fetching, fetchingFail, fetchingSuccess };
}

function* getNOCThresholdData() {
  const mode = yield select(nocModeSelector);
  const role = yield select(selectedUserRoleSelector);
  const { fetching, fetchingFail } = yield getFetchingActions();
  yield put(fetching());
  const payload = yield select(thresholdPayloadSelector);
  const scope =
    mode === modes.TRENDS ? types.TRENDS_THRESHOLD_SCOPE : types.FILTERS_THRESHOLD_SCOPE;

  try {
    yield autoRequest(getThresholdData, scope, payload);
  } catch (err) {
    yield call(message.error, err.message || 'Unknown error.');
    yield put(fetchingFail());
  }
  const isAdmin = role === ADMIN_ROLE_NAME;
  yield put(setAdminProfile(isAdmin));
}

function* saveNOCThresholdData(action) {
  const mode = yield select(nocModeSelector);
  let filters = null;
  const { fetching, fetchingSuccess, fetchingFail } = yield getFetchingActions();

  if (mode === modes.FILTERS) {
    filters = yield select(thresholdFilterSelector);
  }
  yield put(fetching());
  const transformedData = transformData(action.thresholdData, filters, mode);
  const payload =
    mode === modes.TRENDS
      ? yield select(trendsSavePayloadSelector)
      : yield select(filtersSavePayloadSelector);
  merge(payload.queries[0], transformedData);
  try {
    yield autoRequest(updateThresholdData, types.SCOPE, payload);
  } catch (err) {
    yield call(message.error, err.message || 'Unknown error.');
    yield put(fetchingFail());
    return;
  }
  yield put(updateThresholdChangeStatus(false));
  yield getNOCThresholdData();
  if (action.callback) {
    yield call(action.callback);
  }
  if (action.type === types.SAVE_THRESHOLD_AND_REDIRECT) {
    action.navigate(NOC_URL);
  }
  yield put(fetchingSuccess());
  yield call(
    message.success,
    'Updated Successfully, it will take around 2 minutes to reflect the changes.',
  );
}

function* setNOCThresholdDisabledStatus(action) {
  const { fetching, fetchingSuccess, fetchingFail } = yield getFetchingActions();
  yield put(fetching());
  const payload = {
    flags: [
      {
        feature_flag_name: DISABLE_THRESHOLD_FEATURE_FLAG,
        value: Number(action.status),
      },
    ],
  };
  try {
    yield autoRequest(updateThresholdDisabledStatus, types.SCOPE, payload);
  } catch (err) {
    yield call(message.error, err.message || 'Unknown error.');
    yield put(fetchingFail());
    return;
  }
  const resp = yield getFeatures();
  yield put(updateFeatures(resp.data));
  yield put(fetchingSuccess());
  yield call(message.success, 'Updated Successfully');
}

function* watchFetching() {
  yield takeLatest([types.GET_THRESHOLD_DATA], getNOCThresholdData);
}

function* watchUpdateThresholdData() {
  yield takeLatest(
    [types.SAVE_THRESHOLD_DATA, types.SAVE_THRESHOLD_AND_REDIRECT],
    saveNOCThresholdData,
  );
}

function* watchSetThresholdDisabledStatus() {
  yield takeLatest([types.SET_THRESHOLD_ACTIVE_STATUS], setNOCThresholdDisabledStatus);
}

export default [watchFetching, watchUpdateThresholdData, watchSetThresholdDisabledStatus];
